import { approxBetween, approxZero, EPSILON, solveQuadratic, type Vec2, vec } from '../math';

import { arcToCircle, isAngleOnArc, fromUnitCircleSpace, toUnitCircleSpace } from './conics';
import { parameterOnLine } from './lines';
import {
  type Arc,
  type Circle,
  circleToEllipse,
  type Ellipse,
  type Line,
  type Ray,
  type Segment,
  segmentToLine,
} from './types';

/**
 * Intersections.
 *
 * Every routine returns an array of points, ordered deterministically (by parameter
 * along the first argument where that is meaningful). Determinism matters more than it
 * might appear: the construction "the second intersection of this line and this circle"
 * is stored by index in the scene document, so a reordering between runs would silently
 * move a student's point to the other side of the circle.
 *
 * Overlapping collinear or concentric inputs have infinitely many common points and
 * return `[]`. Callers that need to distinguish "no intersection" from "coincident"
 * should ask directly (`areLinesCoincident`, `areCirclesCoincident`).
 */

const NONE: readonly Vec2[] = Object.freeze([]);

/* ------------------------------------------------------------- line-like pairs */

/**
 * Infinite line against infinite line.
 *
 * Returns `null` for parallel or coincident lines. Solved with the cross-product form
 * rather than by comparing slopes, which has no special case for vertical lines.
 */
export function lineLine(a: Line, b: Line, epsilon: number = EPSILON): Vec2 | null {
  const denominator = vec.cross(a.direction, b.direction);
  if (approxZero(denominator, epsilon)) return null;
  const offset = vec.subtract(b.origin, a.origin);
  const t = vec.cross(offset, b.direction) / denominator;
  return vec.addScaled(a.origin, a.direction, t);
}

/**
 * Segment against segment.
 *
 * Both parameters are computed and range-checked, so touching endpoints count as an
 * intersection while a crossing of the *extended* lines outside either segment does not.
 */
export function segmentSegment(a: Segment, b: Segment, epsilon: number = EPSILON): Vec2 | null {
  const da = vec.subtract(a.b, a.a);
  const db = vec.subtract(b.b, b.a);
  const denominator = vec.cross(da, db);

  // Scale the parallel test with the segment magnitudes: the cross product of two long
  // segments is large even when the angle between them is tiny.
  const scale = Math.max(1, vec.length(da) * vec.length(db));
  if (approxZero(denominator / scale, epsilon)) return null;

  const offset = vec.subtract(b.a, a.a);
  const t = vec.cross(offset, db) / denominator;
  const u = vec.cross(offset, da) / denominator;

  if (!approxBetween(t, 0, 1, epsilon) || !approxBetween(u, 0, 1, epsilon)) return null;
  return vec.lerp(a.a, a.b, t);
}

export function lineSegment(l: Line, s: Segment, epsilon: number = EPSILON): Vec2 | null {
  const sl = segmentToLine(s);
  if (sl === null) return null;
  const p = lineLine(l, sl, epsilon);
  if (p === null) return null;
  const d = vec.subtract(s.b, s.a);
  const lenSq = vec.lengthSquared(d);
  if (lenSq <= epsilon * epsilon) return null;
  const t = vec.dot(vec.subtract(p, s.a), d) / lenSq;
  return approxBetween(t, 0, 1, epsilon) ? p : null;
}

export function lineRay(l: Line, r: Ray, epsilon: number = EPSILON): Vec2 | null {
  const p = lineLine(l, { origin: r.origin, direction: r.direction }, epsilon);
  if (p === null) return null;
  const t = vec.dot(vec.subtract(p, r.origin), r.direction);
  return t >= -epsilon ? p : null;
}

export function rayRay(a: Ray, b: Ray, epsilon: number = EPSILON): Vec2 | null {
  const p = lineLine(
    { origin: a.origin, direction: a.direction },
    { origin: b.origin, direction: b.direction },
    epsilon
  );
  if (p === null) return null;
  const ta = vec.dot(vec.subtract(p, a.origin), a.direction);
  const tb = vec.dot(vec.subtract(p, b.origin), b.direction);
  return ta >= -epsilon && tb >= -epsilon ? p : null;
}

export function raySegment(r: Ray, s: Segment, epsilon: number = EPSILON): Vec2 | null {
  const p = lineSegment({ origin: r.origin, direction: r.direction }, s, epsilon);
  if (p === null) return null;
  const t = vec.dot(vec.subtract(p, r.origin), r.direction);
  return t >= -epsilon ? p : null;
}

/* ------------------------------------------------------------ line and circle */

/**
 * Infinite line against circle: 0, 1 (tangent) or 2 points, ordered by parameter along
 * the line.
 *
 * Formulated as a quadratic in the line parameter and handed to the stable solver,
 * which matters at tangency — the case where the discriminant nearly vanishes and the
 * textbook formula loses most of its precision.
 */
export function lineCircle(l: Line, c: Circle, epsilon: number = EPSILON): readonly Vec2[] {
  const toCenter = vec.subtract(l.origin, c.center);
  // |origin + t·direction - center|² = r², with |direction| = 1.
  const a = 1;
  const b = 2 * vec.dot(l.direction, toCenter);
  const cc = vec.lengthSquared(toCenter) - c.radius * c.radius;

  const roots = solveQuadratic(a, b, cc, epsilon);
  if (roots.length === 0) return NONE;
  return roots.map((t) => vec.addScaled(l.origin, l.direction, t));
}

export function segmentCircle(s: Segment, c: Circle, epsilon: number = EPSILON): readonly Vec2[] {
  const l = segmentToLine(s);
  if (l === null) return NONE;
  const d = vec.subtract(s.b, s.a);
  const lenSq = vec.lengthSquared(d);
  if (lenSq <= epsilon * epsilon) return NONE;
  return lineCircle(l, c, epsilon).filter((p) => {
    const t = vec.dot(vec.subtract(p, s.a), d) / lenSq;
    return approxBetween(t, 0, 1, epsilon);
  });
}

export function rayCircle(r: Ray, c: Circle, epsilon: number = EPSILON): readonly Vec2[] {
  const l: Line = { origin: r.origin, direction: r.direction };
  return lineCircle(l, c, epsilon).filter(
    (p) => vec.dot(vec.subtract(p, r.origin), r.direction) >= -epsilon
  );
}

/* --------------------------------------------------------------- circle pairs */

/**
 * Circle against circle: 0, 1 (tangent) or 2 points.
 *
 * Uses the radical-line construction: the intersection points lie on the line
 * perpendicular to the centre-to-centre axis at distance `a` from the first centre,
 * offset by `±h` along that perpendicular. This is numerically better behaved than
 * substituting one circle equation into the other.
 *
 * Concentric or coincident circles return `[]`.
 */
export function circleCircle(a: Circle, b: Circle, epsilon: number = EPSILON): readonly Vec2[] {
  const between = vec.subtract(b.center, a.center);
  const d = vec.length(between);

  // Concentric: either no intersection or the same circle (infinitely many).
  if (approxZero(d, epsilon)) return NONE;

  const sum = a.radius + b.radius;
  const diff = Math.abs(a.radius - b.radius);

  // Scale tangency tolerance with the size of the circles.
  const scale = Math.max(1, sum);
  if (d > sum + epsilon * scale) return NONE; // separate
  if (d < diff - epsilon * scale) return NONE; // one strictly inside the other

  const dir = vec.scale(between, 1 / d);
  const aLen = (d * d - b.radius * b.radius + a.radius * a.radius) / (2 * d);
  const hSq = a.radius * a.radius - aLen * aLen;

  const base = vec.addScaled(a.center, dir, aLen);

  // Tangency: the perpendicular offset vanishes.
  if (hSq <= epsilon * scale) return Object.freeze([base]);

  const h = Math.sqrt(hSq);
  const perp = vec.perpendicular(dir);
  return Object.freeze([vec.addScaled(base, perp, h), vec.addScaled(base, perp, -h)]);
}

export function areCirclesCoincident(a: Circle, b: Circle, epsilon: number = EPSILON): boolean {
  return vec.equals(a.center, b.center, epsilon) && Math.abs(a.radius - b.radius) <= epsilon;
}

/* ------------------------------------------------------------------------ arcs */

/** Keeps only those points of the underlying circle that fall within the arc's sweep. */
function restrictToArc(points: readonly Vec2[], a: Arc, epsilon: number): readonly Vec2[] {
  return points.filter((p) => isAngleOnArc(a, vec.angle(vec.subtract(p, a.center)), epsilon));
}

export function lineArc(l: Line, a: Arc, epsilon: number = EPSILON): readonly Vec2[] {
  return restrictToArc(lineCircle(l, arcToCircle(a), epsilon), a, epsilon);
}

export function segmentArc(s: Segment, a: Arc, epsilon: number = EPSILON): readonly Vec2[] {
  return restrictToArc(segmentCircle(s, arcToCircle(a), epsilon), a, epsilon);
}

export function circleArc(c: Circle, a: Arc, epsilon: number = EPSILON): readonly Vec2[] {
  return restrictToArc(circleCircle(c, arcToCircle(a), epsilon), a, epsilon);
}

export function arcArc(a: Arc, b: Arc, epsilon: number = EPSILON): readonly Vec2[] {
  const candidates = circleCircle(arcToCircle(a), arcToCircle(b), epsilon);
  return restrictToArc(restrictToArc(candidates, a, epsilon), b, epsilon);
}

/* -------------------------------------------------------------------- ellipses */

/**
 * Line against ellipse.
 *
 * Mapped into the ellipse's unit-circle space, where the problem becomes line-against-
 * unit-circle, then mapped back. The affine map preserves incidence, so the result is
 * exact rather than approximate — and it costs one quadratic instead of the general
 * conic machinery.
 */
export function lineEllipse(l: Line, e: Ellipse, epsilon: number = EPSILON): readonly Vec2[] {
  if (e.rx <= epsilon || e.ry <= epsilon) return NONE;

  const o = toUnitCircleSpace(e, l.origin);
  // A direction transforms without the translation component.
  const tip = toUnitCircleSpace(e, vec.add(l.origin, l.direction));
  const d = vec.subtract(tip, o);
  if (vec.isZero(d, epsilon)) return NONE;

  const a = vec.lengthSquared(d);
  const b = 2 * vec.dot(o, d);
  const c = vec.lengthSquared(o) - 1;

  const roots = solveQuadratic(a, b, c, epsilon);
  if (roots.length === 0) return NONE;

  return roots.map((t) => fromUnitCircleSpace(e, vec.addScaled(o, d, t)));
}

export function segmentEllipse(s: Segment, e: Ellipse, epsilon: number = EPSILON): readonly Vec2[] {
  const l = segmentToLine(s);
  if (l === null) return NONE;
  const d = vec.subtract(s.b, s.a);
  const lenSq = vec.lengthSquared(d);
  if (lenSq <= epsilon * epsilon) return NONE;
  return lineEllipse(l, e, epsilon).filter((p) => {
    const t = vec.dot(vec.subtract(p, s.a), d) / lenSq;
    return approxBetween(t, 0, 1, epsilon);
  });
}

export function circleEllipse(c: Circle, e: Ellipse, epsilon: number = EPSILON): readonly Vec2[] {
  return ellipseEllipse(circleToEllipse(c), e, epsilon);
}

/**
 * Ellipse against ellipse.
 *
 * Two general conics meet in up to four points, and the exact solution requires finding
 * the roots of a degree-four resultant — the one place in this module where a closed
 * form is impractical to implement reliably.
 *
 * Instead: map into the first ellipse's unit-circle space, where the first curve becomes
 * the unit circle, and sample the second curve's image for sign changes of
 * `|p|² - 1`, refining each bracket by bisection. Bisection rather than Newton because
 * it cannot diverge near the tangencies that a geometry editor produces constantly
 * (two circles snapped to touch), and 60 samples over the parameter range reliably
 * separates all four roots for the aspect ratios a diagram uses.
 *
 * Accuracy is ~1e-12 in the unit space. Documented here rather than hidden: this is the
 * only approximate intersection routine in the module.
 */
export function ellipseEllipse(a: Ellipse, b: Ellipse, epsilon: number = EPSILON): readonly Vec2[] {
  if (a.rx <= epsilon || a.ry <= epsilon || b.rx <= epsilon || b.ry <= epsilon) return NONE;

  const SAMPLES = 60;
  const TAU = Math.PI * 2;

  // f(t) < 0 inside the unit circle, > 0 outside.
  const f = (t: number): number => {
    const world = {
      x:
        b.center.x +
        b.rx * Math.cos(t) * Math.cos(b.rotation) -
        b.ry * Math.sin(t) * Math.sin(b.rotation),
      y:
        b.center.y +
        b.rx * Math.cos(t) * Math.sin(b.rotation) +
        b.ry * Math.sin(t) * Math.cos(b.rotation),
    };
    return vec.lengthSquared(toUnitCircleSpace(a, world)) - 1;
  };

  const results: Vec2[] = [];
  let prevT = 0;
  let prevF = f(0);

  for (let i = 1; i <= SAMPLES; i++) {
    const t = (i / SAMPLES) * TAU;
    const cur = f(t);

    if (approxZero(cur, epsilon)) {
      pushUnique(results, evalEllipse(b, t), epsilon);
    } else if (prevF * cur < 0) {
      pushUnique(results, evalEllipse(b, bisect(f, prevT, t)), epsilon);
    }

    prevT = t;
    prevF = cur;
  }

  return results.length === 0 ? NONE : Object.freeze(results);
}

function evalEllipse(e: Ellipse, t: number): Vec2 {
  const cos = Math.cos(e.rotation);
  const sin = Math.sin(e.rotation);
  const x = e.rx * Math.cos(t);
  const y = e.ry * Math.sin(t);
  return { x: e.center.x + x * cos - y * sin, y: e.center.y + x * sin + y * cos };
}

function bisect(f: (t: number) => number, lo: number, hi: number): number {
  let a = lo;
  let b = hi;
  let fa = f(a);
  for (let i = 0; i < 80; i++) {
    const mid = (a + b) / 2;
    const fm = f(mid);
    if (fm === 0) return mid;
    if (fa * fm < 0) {
      b = mid;
    } else {
      a = mid;
      fa = fm;
    }
  }
  return (a + b) / 2;
}

function pushUnique(into: Vec2[], p: Vec2, epsilon: number): void {
  for (const existing of into) {
    if (vec.equals(existing, p, Math.max(epsilon, 1e-7))) return;
  }
  into.push(p);
}

/* ------------------------------------------------------------------ utilities */

/**
 * Sorts intersection points by their parameter along a line.
 *
 * Callers that combine results from several routines need a single deterministic order
 * before storing indices in a construction definition.
 */
export function sortAlongLine(points: readonly Vec2[], l: Line): readonly Vec2[] {
  return [...points].sort((p, q) => parameterOnLine(l, p) - parameterOnLine(l, q));
}
